import {AllStates, ReactState} from "../Forms";
import * as React from "react";
import {IconButton, PaletteColor, Paper, Typography, useTheme} from "@mui/material";
import Stack from "@mui/material/Stack";

import requirementsDefinitions from './requirements.json';
import {Requirements, Translatable} from "./Requirements";
import {Mapping, RequiredModuleProportion} from "./Mappings";
import {NestedCard} from "./NestedCard";
import {getDegreeProgramsArray, getProgramTitle} from "../degrees/Degrees";
import DeleteIcon from '@mui/icons-material/Delete';
import Grid from "@mui/material/Grid2";
import {AddMappedModuleButton} from "./AddMappedModuleButton";
import {SelectCreditsButton} from "./SelectCreditsButton";
import {useTolgee, useTranslate} from "@tolgee/react";
import {SMALL_WINDOW_WIDTH} from "../Representation";

interface MappingsPageProps {
    mappingsState: ReactState<any>,
    allStates: AllStates,
    windowWidth: number
}

export interface ProjectedRequiredModuleProportion {
    required_module_key: string
    applicant_credits: number
}

export interface MappableApplicantModule {
    degreeProgramIndex: number,
    title: string,
    proportions: ProjectedRequiredModuleProportion[],
    credits: number
    url?: string
}

interface MappableRequiredModule {
    key: string,
    otherKeys: string[],
    title: Translatable,
    urls: Translatable[],
    requiredEcts: number | undefined
}

const NA = "NOT APPLICABLE";

export function activeApplicantModuleMappingsByProgram(allStates: AllStates) {
    // an array of the titles (unique identifier) of the degree programs specified in the page "degrees"
    const programs = getDegreeProgramsArray(allStates.degrees[0]);

    // the array corresponds to the degree programs, i.e., it has the same length and the MappableApplicantModule[] at index i is for program i
    const applicantModulesByProgram: MappableApplicantModule[][] = new Array<MappableApplicantModule[]>(programs.length);
    applicantModulesByProgram.fill([]);

    allStates.modules[0]
        // only for programs that are currently configured
        .filter(moduleList => moduleList.degree_program_index < programs.length)
        // create the array of MappableApplicantModule and write it to applicantModulesByProgram
        .forEach(moduleList => {
            const programModules: MappableApplicantModule[] = [];
            applicantModulesByProgram[moduleList.degree_program_index] = programModules;

            // create a MappableApplicantModule for each module
            moduleList.modules.forEach(module => {
                programModules.push({
                    degreeProgramIndex: moduleList.degree_program_index,
                    title: module.title ?? "TITLE MISSING",
                    credits: module.credits ?? 0,
                    url: module.module_url,
                    // find mapping for this module and insert "proportions"
                    // the module is identified by its title and the index of the program to which it belongs
                    proportions: (
                        allStates.mappings[0]
                            .find(mapping => (
                                mapping.applicant_module_degree_program_index === moduleList.degree_program_index
                                && mapping.applicant_module_title === module.title)
                            )
                            // get the mapped required modules or an empty array if no mapping for this module is present
                            ?.mapped_required_modules ?? [])
                        // transform all applicable mappings to ProjectedRequiredModuleProportion
                        .map(proportion => (
                            {
                                // if no effective key was found, fill in NA as marker to filter out in next step
                                required_module_key: getEffectiveKey(proportion, allStates) ?? NA,
                                applicant_credits: proportion.applicant_credits
                            }
                        ))
                        // remove proportions that don't have an available key in the target program's required modules
                        .filter(proportion => proportion.required_module_key !== NA)
                })
            });
        });

    return applicantModulesByProgram;
}

export function getApplicableRequirementsDefinition(allStates: AllStates): Requirements {
    return requirementsDefinitions.find(req => req.key === allStates.base[0].study_program.subject) ??
        requirementsDefinitions[0]
}

function getEffectiveKey(proportion: RequiredModuleProportion, allStates: AllStates) {
    const applicableKeys = getApplicableRequirementsDefinition(allStates).areas.flatMap(area => area.modules).map(module => module.key);

    // all the potential keys (the primary, exact key and possibly alternative, equivalent ones if the target study program does not include the exact module)
    // the highest priority (best match) is first in the array
    return [proportion.required_module_key, ...(proportion.required_module_other_keys ?? [])]
        // search the first candidate key that is available in the target program's required modules
        .find(key => applicableKeys.indexOf(key) >= 0);
}

function MappingsPage(
    {
        mappingsState,
        allStates,
        windowWidth
    }: MappingsPageProps) {

    const [mappings, setMappings] = mappingsState as ReactState<Mapping[]>;

    const {t} = useTranslate();
    const lang = useTolgee().getLanguage() ?? "en";

    const theme = useTheme();
    const applicantModuleColor = theme.palette.secondary;
    const requiredModuleColor = theme.palette.secondary;
    const areaColor: PaletteColor = {main: '#bbbbbb', light: "#dddddd", dark: '#999999', contrastText: '#000000' };

    ////////
    // Precompute some lookup tables and lists
    ////////

    // UMR requirements
    const requirements: Requirements = getApplicableRequirementsDefinition(allStates);
    const applicableKeys = requirements.areas
        .flatMap(area => area.modules)
        .map(module => module.key);

    // a mapping of degree-program-title to an array of applicant modules
    const applicantModulesByProgram = activeApplicantModuleMappingsByProgram(allStates);
    const programs = getDegreeProgramsArray(allStates.degrees[0]);

    const requiredModules: MappableRequiredModule[] = requirements.areas.flatMap(area => area.modules).map(module => ({
        key: module.key,
        otherKeys: module.otherKeys,
        title: module.title,
        urls: module.urls ?? [],
        requiredEcts: module.credits
    }));

    const requiredModule = (module : MappableRequiredModule) => {
        const mappedCredits = applicantModulesByProgram
            .flatMap((applicantModuleList) => applicantModuleList)
            .filter(applicantModule => applicantModule.proportions.some(proportion => proportion.required_module_key === module.key))
            .map(rowMapping => (
                rowMapping.proportions
                                .find(proportion => proportion.required_module_key === module.key)
                                ?.applicant_credits ?? 0)).reduce((accumulator, currentValue) => accumulator + currentValue, 0);



        return (<NestedCard
            title={module.title[lang]}
            urls={module.urls.map(url => url[lang])}
            key={module.key}
            headerColor={mappedCredits === 0 ? requiredModuleColor.light : requiredModuleColor.main}
            headerTextColor={requiredModuleColor.contrastText}
            subtitle={mappedCredits === 0 ? t("nothing_mapped_to_module") : undefined}
        >
            <Grid container spacing={2} columns={12}>
                {applicantModulesByProgram
                    .flatMap((applicantModuleList) => applicantModuleList)
                    .filter(applicantModule => applicantModule.proportions.some(proportion => proportion.required_module_key === module.key))
                    .map(rowMapping => (
                        <React.Fragment
                            key={getProgramTitle(programs[rowMapping.degreeProgramIndex]) + ":" + rowMapping.title}>
                            <Grid size={windowWidth > SMALL_WINDOW_WIDTH ? 9 : 12}>
                                <Typography variant="body1">
                                    {rowMapping.title}
                                </Typography>
                                <Typography variant="body2" sx={{fontStyle: "italic"}}>
                                    {getProgramTitle(programs[rowMapping.degreeProgramIndex])}
                                </Typography>
                            </Grid>
                            <Grid size={windowWidth > SMALL_WINDOW_WIDTH ? 2 : "grow"}>
                                {
                                    (rowMapping.proportions
                                        .find(proportion => proportion.required_module_key === module.key)
                                        ?.applicant_credits ?? 0).toString() + " " +
                                    t("credit_points")
                                }
                            </Grid>
                            <Grid size={1}>
                                <IconButton
                                    edge="start"
                                    aria-label="delete"
                                    onClick={() => {
                                        setMappings(mappings.map(mapping => {
                                                if (mapping.applicant_module_title === rowMapping.title &&
                                                    mapping.applicant_module_degree_program_index === rowMapping.degreeProgramIndex) {
                                                    return {
                                                        ...mapping,
                                                        mapped_required_modules: mapping.mapped_required_modules.filter(mappedProportioning => (
                                                            [mappedProportioning.required_module_key, ...(mappedProportioning.required_module_other_keys ?? [])]
                                                                .find(key => applicableKeys.indexOf(key) >= 0) !== module.key
                                                        ))
                                                    };
                                                } else {
                                                    return mapping;
                                                }
                                            }
                                        ));
                                    }}>
                                    <DeleteIcon/>
                                </IconButton>
                            </Grid>
                        </React.Fragment>
                    ))}
            </Grid>
        </NestedCard>);
    }


    return <React.Fragment>
        <Stack direction="row" spacing={2}>
            <Paper sx={{width: '50%', p: 2}} key={"paper-applicant-modules"}>
                <Typography variant="h5">{t("your_modules")}</Typography>
                <Stack spacing={2}>
                    {applicantModulesByProgram.map((mappableModules, programIndex) => {
                        const countIncompletelyMapped = mappableModules.filter(module => {
                            const mapping = mappings.find(mapping => mapping.applicant_module_degree_program_index === module.degreeProgramIndex &&
                                mapping.applicant_module_title === module.title);
                            const unmappedCredits = module.credits - (mapping ? mapping.mapped_required_modules.reduce((acc, v) => acc + v.applicant_credits, 0) : 0)
                            return unmappedCredits > 0;
                        }).length;
                        return <React.Fragment key={getProgramTitle(programs[programIndex])}>
                            <NestedCard
                                key={getProgramTitle(programs[programIndex])}
                                title={getProgramTitle(programs[programIndex])}
                                headerColor={countIncompletelyMapped > 0 ? areaColor.light : areaColor.main}
                                headerTextColor={areaColor.contrastText}
                                subtitle={countIncompletelyMapped > 0 ? t("not_all_mapped_for_program") : undefined}>
                                {mappableModules.map(module => {
                                    const mapping = mappings.find(mapping => mapping.applicant_module_degree_program_index === module.degreeProgramIndex &&
                                        mapping.applicant_module_title === module.title);
                                    const unmappedCredits = module.credits - (mapping ? mapping.mapped_required_modules.reduce((acc, v) => acc + v.applicant_credits, 0) : 0)

                                    return <NestedCard
                                        key={module.title}
                                        title={module.title}
                                        headerColor={unmappedCredits > 0 ? applicantModuleColor.light : applicantModuleColor.main} // todo
                                        headerTextColor={applicantModuleColor.contrastText}
                                        subtitle={unmappedCredits > 0 ? t("not_all_mapped_for_module") : undefined}
                                        action={
                                            <AddMappedModuleButton
                                                key={module.title}
                                                options={requiredModules.filter(requiredModule => (

                                                    !module.proportions.some(
                                                        proportion =>
                                                            proportion.required_module_key === requiredModule.key)
                                                )).map(requiredModule => ({
                                                    key: requiredModule.key,
                                                    otherKeys: requiredModule.otherKeys,
                                                    translatedTitle: requiredModule.title[lang]
                                                }))}
                                                onAdd={(key, otherKeys) => {
                                                    const newProportion: RequiredModuleProportion = {
                                                        required_module_key: key,
                                                        required_module_other_keys: otherKeys,
                                                        applicant_credits: unmappedCredits
                                                    };
                                                    const newMapping = mapping ?
                                                        {
                                                            ...mapping,
                                                            mapped_required_modules: [...mapping.mapped_required_modules, newProportion]
                                                        } :
                                                        {
                                                            applicant_module_degree_program_index: module.degreeProgramIndex,
                                                            applicant_module_title: module.title,
                                                            mapped_required_modules: [newProportion]
                                                        };
                                                    setMappings(
                                                        [
                                                            ...mappings.filter(mapping => !(mapping.applicant_module_degree_program_index === module.degreeProgramIndex &&
                                                                mapping.applicant_module_title === module.title)),
                                                            newMapping]);
                                                }}/>
                                        }
                                    >

                                        {/* List of required modules to which "module" is mapped. */}
                                        <Grid container spacing={2} columns={12}>
                                            {
                                                (applicantModulesByProgram[programIndex])
                                                    .filter(mappedApplicantModule => mappedApplicantModule.title === module.title)
                                                    .map(mapping => (
                                                        mapping.proportions.map(proportion =>
                                                            <React.Fragment key={proportion.required_module_key}>
                                                                <Grid size={windowWidth > SMALL_WINDOW_WIDTH ? 9 : 12}>
                                                                    {
                                                                        requiredModules.find(requiredModule => (
                                                                            requiredModule.key === proportion.required_module_key))?.title[lang]
                                                                    }
                                                                </Grid>
                                                                <Grid
                                                                    size={windowWidth > SMALL_WINDOW_WIDTH ? 2 : "grow"}>
                                                                    <SelectCreditsButton
                                                                        maxValue={module.credits}
                                                                        maxEnabled={
                                                                            module.credits - (mapping ? mapping.proportions
                                                                                .filter(mapping => mapping.required_module_key !== proportion.required_module_key)
                                                                                .reduce((acc, v) => acc + v.applicant_credits, 0) : 0)
                                                                        }
                                                                        selected={proportion.applicant_credits}
                                                                        onSelect={credits => {
                                                                            setMappings(mappings.map(mapping => {
                                                                                if (mapping.applicant_module_title === module.title &&
                                                                                    mapping.applicant_module_degree_program_index === module.degreeProgramIndex) {
                                                                                    return {
                                                                                        ...mapping,
                                                                                        mapped_required_modules: mapping.mapped_required_modules.map(mappedProportioning => {
                                                                                            if (getEffectiveKey(mappedProportioning, allStates) === proportion.required_module_key) {
                                                                                                return {
                                                                                                    ...mappedProportioning,
                                                                                                    applicant_credits: credits
                                                                                                };
                                                                                            } else {
                                                                                                return mappedProportioning;
                                                                                            }
                                                                                        })

                                                                                    };
                                                                                } else {
                                                                                    return mapping;
                                                                                }
                                                                            }));
                                                                        }}
                                                                    />
                                                                </Grid>
                                                                <Grid size={1}>
                                                                    <IconButton
                                                                        edge="start"
                                                                        aria-label="delete"
                                                                        onClick={() => {
                                                                            setMappings(mappings.map(mapping => {
                                                                                if (mapping.applicant_module_title === module.title &&
                                                                                    mapping.applicant_module_degree_program_index === module.degreeProgramIndex) {
                                                                                    return {
                                                                                        ...mapping,
                                                                                        mapped_required_modules: mapping.mapped_required_modules.filter(mappedProportioning => (
                                                                                            getEffectiveKey(mappedProportioning, allStates)!== proportion.required_module_key
                                                                                        ))
                                                                                    };
                                                                                } else {
                                                                                    return mapping;
                                                                                }
                                                                            }));
                                                                        }}>
                                                                        <DeleteIcon/>
                                                                    </IconButton>
                                                                </Grid>
                                                            </React.Fragment>
                                                        )
                                                    ))
                                            }
                                        </Grid>

                                    </NestedCard>
                                })}
                            </NestedCard>
                        </React.Fragment>}
                    )}
                </Stack>

            </Paper>
            <Paper sx={{width: '50%', p: 2}} key={"paper-required-modules"}>
                <Typography variant="h5" key={"title"}>{t("required_modules")}</Typography>
                <Stack spacing={2}>
                    {requirements.areas.map(area => {
                        const areaModuleKeys = area.modules.map(module => module.key);
                        const mappedCredits = applicantModulesByProgram
                            .flatMap((applicantModuleList) => applicantModuleList)
                            .filter(applicantModule => applicantModule.proportions.some(proportion => areaModuleKeys.includes(proportion.required_module_key)))
                            .map(rowMapping => (
                                rowMapping.proportions
                                    .find(proportion => areaModuleKeys.includes(proportion.required_module_key))
                                    ?.applicant_credits ?? 0)).reduce((accumulator, currentValue) => accumulator + currentValue, 0);

                        return <NestedCard
                            key={area.title['de']}
                            title={area.title[lang]}
                            headerColor={mappedCredits === 0 ? areaColor.light : areaColor.main}
                            headerTextColor={areaColor.contrastText}
                            subtitle={mappedCredits === 0 ? t("nothing_mapped_to_area") : undefined}
                        >
                            {area.modules.map(module => ({
                                key: module.key,
                                otherKeys: module.otherKeys,
                                title: module.title,
                                urls: module.urls ?? [],
                                requiredEcts: module.credits
                            })).map(module => requiredModule(module))}
                        </NestedCard>})}
                </Stack>
            </Paper>
        </Stack>
    </React.Fragment>
        ;
}

export default MappingsPage;